import { CheckCircleOutlined, CheckOutlined, PlusOutlined } from '@ant-design/icons'
import { useDebounce } from 'ahooks'
import { AutoComplete, Button, Modal, Tag, message } from 'antd'
import { getOperationName } from 'apollo-utilities'
import produce from 'immer'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetUserHobbiesDocument,
  useGetFrequentHobbiesQuery,
  useGetHobbySearchQuery,
  useHobbyCreateMutation,
  useUserHobbyBatchUpdateMutation,
} from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import s from './user-info-hobby-modal.module.less'

export const UserInfoHobbyModal: React.FC<{
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  userId: number
  userHobbies: { name: string; hobbyId: number }[]
}> = ({ visible, setVisible, userId, userHobbies }) => {
  const { t } = useTranslation()
  const [hobbies, setHobbies] = React.useState<{ hobbyId: number; name: string; isSelected: boolean }[]>([])

  const [userHobbyBatchUpdate] = useUserHobbyBatchUpdateMutation({
    refetchQueries: [getOperationName(GetUserHobbiesDocument)!],
    onCompleted() {
      const selectedHobbies = hobbies.filter((hobby) => hobby.isSelected)
      if (selectedHobbies.length > 0) {
        const category = traceEvent.profile
        ga('event', category.events.click_add_hobbies, {
          event_category: category.event_category,
        })
      }
      setVisible(false)
    },
    onError() {
      setVisible(false)
    },
  })

  const onOK = () => {
    if (hobbies.filter((hobby) => hobby.isSelected).length > 0) {
      userHobbyBatchUpdate({
        variables: {
          userId,
          hobbyIds: hobbies.filter((hobby) => hobby.isSelected).map((hobby) => hobby.hobbyId),
        },
      })
    }
  }

  const footer = (
    <div className={s.footer}>
      <Button type='text' onClick={() => setVisible(false)}>
        {t('user.info.cancel')}
      </Button>
      <Button type='primary' onClick={() => onOK()}>
        {t('user.info.save')}
      </Button>
    </div>
  )
  return (
    <Modal
      title={t('user.info.hobby.addTag')}
      visible={visible}
      footer={footer}
      closable={false}
      maskClosable={false}
      destroyOnClose
    >
      <UserInfoHobbyModalInner userId={userId} userHobbies={userHobbies} hobbies={hobbies} setHobbies={setHobbies} />
    </Modal>
  )
}

export const UserInfoHobbyModalInner: React.FC<{
  userId: number
  userHobbies: { name: string; hobbyId: number }[]
  hobbies: { hobbyId: number; name: string; isSelected: boolean }[]
  setHobbies: React.Dispatch<
    React.SetStateAction<
      {
        hobbyId: number
        name: string
        isSelected: boolean
      }[]
    >
  >
}> = ({ userId, userHobbies, hobbies, setHobbies }) => {
  const { t } = useTranslation()
  const [showSearch, setShowSearch] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 500 })

  const { data: frequentHobbyData } = useGetFrequentHobbiesQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        top: 10,
      },
      userId,
    },
  })

  const { data } = useGetHobbySearchQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        search: debouncedSearch,
      },
      userId,
    },
  })

  const [hobbyCreate] = useHobbyCreateMutation({
    onCompleted(data) {
      const hobby = data.hobbyCreate
      if (hobby) {
        setHobbies((hobbies) => [...hobbies.slice(), { ...hobby, isSelected: true }])
        message.info('添加爱好成功')
      } else {
        message.warn('添加爱好失败，可能原因：已存在')
      }
    },
  })

  React.useEffect(() => {
    if (frequentHobbyData) {
      setHobbies(frequentHobbyData.hobbies.map((hobby) => ({ ...hobby, isSelected: false })))
    }
  }, [frequentHobbyData])

  const hobbyCandidates = data?.hobbies.slice(0, 6) || []

  const options =
    hobbyCandidates.length > 0
      ? hobbyCandidates.map((value) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <span>{value.name}</span>
              {userHobbies.some((userHobby) => userHobby.hobbyId === value.hobbyId) && (
                <Tag style={{ border: 0, marginLeft: 8 }} icon={<CheckCircleOutlined />} color='success'>
                  {t('user.info.skill.alreadyAdded')}
                </Tag>
              )}
            </div>
          ),
          value: value.hobbyId,
        }))
      : [{ label: <span>{search}</span>, value: 0 }]

  const searchComponent = (
    <div className={s.searchWrapper}>
      {showSearch ? (
        <AutoComplete
          defaultActiveFirstOption={true}
          filterOption={false}
          value={search}
          placeholder={t('user.info.hobby.searchPlaceholder')}
          className={s.search}
          onSelect={(id: any) => {
            if (Number(id) === 0 && search !== '') {
              hobbyCreate({
                variables: {
                  name: search,
                },
              })
              return
            }

            // 选择的技能已存在
            if (userHobbies.some((userHobby) => userHobby.hobbyId === Number(id))) {
              return
            }

            const candidate = hobbyCandidates.find((candidate) => candidate.hobbyId === Number(id))
            if (candidate) {
              setSearch(candidate.name)
              // 选择的技能已在添加列表
              let newState = hobbies.slice()
              if (hobbies.some((hobby) => hobby.hobbyId === Number(id))) {
                newState = produce(hobbies, (draft) => {
                  const hobby = draft.find((hobby) => hobby.hobbyId === Number(id))
                  hobby!.isSelected = !hobby!.isSelected
                })
              } else {
                newState = produce(hobbies, (draft) => {
                  draft.push({
                    ...hobbyCandidates.find((candidate) => candidate.hobbyId === Number(id))!,
                    isSelected: true,
                  })
                })
              }

              setHobbies(newState)
            }
          }}
          options={options}
          onSearch={(value) => {
            setSearch(value)
          }}
        />
      ) : (
        <Button type='text' icon={<PlusOutlined />} size='large' onClick={() => setShowSearch(true)}>
          {t('user.info.hobby.addTag')}
        </Button>
      )}
    </div>
  )

  const frequentTags = hobbies.length > 0 && (
    <div className={s.frequentWrapper}>
      <div className={s.title}>
        <span style={{ color: '#65676b' }}>{t('user.info.hobby.frequent')}</span>
      </div>
      <div className={s.hobbies}>
        {hobbies.map((hobby) => {
          const isSelected = hobbies.some((h) => h.hobbyId === hobby.hobbyId && h.isSelected)
          const Icon = isSelected ? CheckOutlined : PlusOutlined
          return (
            <Tag
              color={isSelected ? '#48bbb5' : undefined}
              style={{ backgroundColor: isSelected ? '#48bbb5' : undefined }}
              onClick={() => {
                const state = produce(hobbies, (draft) => {
                  const selectedHobby = draft.find((h) => h.hobbyId === hobby.hobbyId)
                  selectedHobby!.isSelected = !selectedHobby!.isSelected
                })
                setHobbies(state)
              }}
              key={hobby.hobbyId}
            >
              <div style={{ padding: 4 }}>
                {hobby.name}
                <Icon style={{ marginLeft: 8 }} />
              </div>
            </Tag>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className={s.modal}>
      {frequentTags}
      {searchComponent}
    </div>
  )
}
